import { useEffect, useRef, useState } from 'react';
import { useSession } from '../App';
import type { Payment } from '../api';
import { Link, useSearch } from '../router';
import { ErrorNotice } from '../components/ui';
import { Rings } from '../components/Rings';
import { formatDate, formatMoney } from '../format';

const POLL_MS = 2000;
const GIVE_UP_MS = 90_000;

/**
 * Where PayU sends the buyer back. Nothing here trusts the URL: PayU's
 * `?error=` is only a hint, and the outcome shown is whatever the API reports
 * after asking PayU itself.
 */
export function PaymentReturnPage({ paymentId }: { paymentId: string }) {
  const { client } = useSession();
  const search = useSearch();
  const providerError = search.get('error');
  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const next = await client.payment(paymentId);
        if (stopped) return;
        setPayment(next);
        setError(null);
        if (next.status !== 'pending') return;
      } catch (nextError) {
        if (stopped) return;
        setError(nextError);
      }
      if (Date.now() - startedAt.current > GIVE_UP_MS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(() => void poll(), POLL_MS);
    };
    void poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [client, paymentId]);

  if (!payment && error) {
    return (
      <div className="result">
        <ErrorNotice error={error} />
        <Link to="/billing" className="button">
          Back to billing
        </Link>
      </div>
    );
  }

  if (!payment || payment.status === 'pending') {
    return (
      <div className="result">
        <Rings state="searching" size={96} />
        <h1>{timedOut ? 'Still waiting for PayU' : 'Confirming your payment…'}</h1>
        <p className="lede">
          {timedOut
            ? 'PayU has not confirmed the payment yet. Bank transfers can take a while; your plan switches on automatically when it arrives, and you can safely close this page.'
            : providerError
              ? 'PayU reported a problem with this payment. Checking what actually happened…'
              : 'This usually takes a few seconds.'}
        </p>
        {payment?.redirectUrl && timedOut && (
          <a className="button" href={payment.redirectUrl}>
            Return to PayU
          </a>
        )}
        <Link to="/billing" className="button ghost">
          Back to billing
        </Link>
      </div>
    );
  }

  if (payment.status === 'completed') {
    return (
      <div className="result">
        <Rings state="connected" size={96}>
          ✓
        </Rings>
        <h1>Payment received</h1>
        <p className="lede">
          {formatMoney(payment.amountMinor, payment.currency)} for {payment.description}.{' '}
          {payment.periodEnd ? `${payment.planName} is active until ${formatDate(payment.periodEnd)}.` : ''}
        </p>
        <p className="muted">Your devices pick up the new features the next time they sync with your account.</p>
        <div className="result-actions">
          <Link to="/" className="button primary">
            Go to overview
          </Link>
          <Link to="/billing" className="button">
            Billing details
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="result">
      <Rings state="lost" size={96}>
        !
      </Rings>
      <h1>{payment.status === 'cancelled' ? 'Payment cancelled' : 'Payment not completed'}</h1>
      <p className="lede">Nothing was charged and your plan has not changed. You can try again whenever you like.</p>
      <div className="result-actions">
        <Link to="/billing" className="button primary">
          Choose a plan again
        </Link>
      </div>
    </div>
  );
}
